import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { Link, Smartphone, Mail, UserPlus, X, ArrowLeft, Search, Send } from 'lucide-react';
import { toShareUrl } from '../../utils/helpers';
import { useTheme } from '../../contexts/ThemeContext';
import { notificationAPI } from '../../services/api';
import { profileAPI } from '../../profile/services';
import { btn } from '../../utils/buttonStyles';
import { Capacitor } from '@capacitor/core';
import { Share } from '@capacitor/share';

/**
 * ShareModal - Generic modal for sharing content via multiple methods
 *
 * Props:
 * - isOpen: boolean - Whether the modal is open
 * - onClose: function - Callback to close the modal
 * - entity: object - Entity data to share { id, name, description, type, slug? }
 * - entityType: string - Type of entity (e.g., 'tournament', 'event', 'squad', 'facility')
 * - urlPattern: string - URL pattern with an {id} and/or {slug} placeholder
 *   (e.g., '/tournament/{id}' or '/academy/{slug}') — pass a matching `entity.slug`
 *   when using {slug}.
 * - currentUser: object - Current logged-in user
 */
// Substitutes both placeholders (whichever the pattern actually contains) so callers
// can share by either a stable id or a human-readable slug.
const buildEntityPath = (pattern, entity) =>
  pattern.replace('{id}', entity?.id).replace('{slug}', entity?.slug);

export default function ShareModal({
  isOpen,
  onClose,
  entity,
  entityType = 'item',
  urlPattern = '/{id}',
  currentUser
}) {
  const { theme } = useTheme();
  const [view, setView] = useState('options'); // 'options', 'referral', or 'phone'

  // On native Capacitor apps, trigger the OS share sheet directly instead of the custom modal.
  // Deliberately omit `text` — Android has no separate "url" slot in its share intent, so
  // when both text and url are set they get concatenated into one EXTRA_TEXT string. That
  // broke the sheet's "Copy" quick action: it copied "Tournament Name — description
  // https://..." instead of a clean link, which browsers then treat as a search query
  // instead of a URL. `title` alone is safe — Android uses it as the email subject, not
  // part of the copyable text.
  useEffect(() => {
    if (!isOpen || !entity || !Capacitor.isNativePlatform()) return;
    const url = toShareUrl(buildEntityPath(urlPattern, entity));
    Share.share({
      title: entity?.name,
      url,
      dialogTitle: `Share ${entityType}`,
    }).finally(() => onClose());
  }, [isOpen]);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [userSearch, setUserSearch] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [referralMessage, setReferralMessage] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [sendingReferrals, setSendingReferrals] = useState(false);
  const [referralStatus, setReferralStatus] = useState({ type: '', message: '' });

  // Generate entity URL
  const getEntityUrl = () => toShareUrl(buildEntityPath(urlPattern, entity));

  // Get entity display name (capitalize first letter)
  const getEntityDisplayName = () => {
    return entityType.charAt(0).toUpperCase() + entityType.slice(1);
  };

  // Reset state when modal closes
  const handleClose = () => {
    setView('options');
    setPhoneNumber('');
    setUserSearch('');
    setSelectedUsers([]);
    setReferralMessage('');
    setSearchResults([]);
    setHasSearched(false);
    setReferralStatus({ type: '', message: '' });
    setSendingReferrals(false);
    onClose();
  };

  // Search users from API
  const searchUsers = async () => {
    if (!userSearch.trim()) {
      toast.error('Please enter a search term');
      return;
    }

    setUsersLoading(true);
    setHasSearched(true);
    try {
      const response = await profileAPI.getAll();
      const users = response?.data || [];

      const filteredUsers = users.filter(user => {
        const fullName = user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.firstName || '';
        const email = user.email || '';
        const searchTerm = userSearch.toLowerCase();
        return fullName.toLowerCase().includes(searchTerm) ||
               email.toLowerCase().includes(searchTerm);
      });

      const transformedUsers = filteredUsers.map(user => ({
        id: user.id,
        name: user.firstName && user.lastName
          ? `${user.firstName} ${user.lastName}`
          : user.firstName || 'Unknown User',
        email: user.email || '',
        avatar: '👤',
        location: 'Location not available',
        addressId: user.addressId || null
      }));

      setSearchResults(transformedUsers);
    } catch (error) {
      console.error('Failed to search users:', error);
      setSearchResults([]);
    } finally {
      setUsersLoading(false);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      searchUsers();
    }
  };

  const handleCopyLink = () => {
    const entityUrl = getEntityUrl();
    navigator.clipboard?.writeText(entityUrl);
    toast.success(`${getEntityDisplayName()} link copied to clipboard!`);
    handleClose();
  };

  const handleSendToPhone = () => {
    setView('phone');
  };

  const handleSendSms = () => {
    if (!phoneNumber.trim()) {
      toast.error('Please enter a phone number');
      return;
    }
    // Just the link — an SMS client's own link preview already shows the
    // page title/description, and a bare URL is unambiguous if the
    // recipient copies the message text.
    // sms: URI opens native SMS app with number and pre-filled body
    window.open(`sms:${phoneNumber.trim()}?body=${encodeURIComponent(getEntityUrl())}`, '_self');
    handleClose();
  };

  const handleSendToEmail = () => {
    // Subject can carry the entity name freely (it's a separate mailto
    // param, never concatenated into copyable link text) — the body is
    // just the link, same rule as every other share path.
    const subject = `${getEntityDisplayName()} Invitation: ${entity?.name}`;
    const mailtoUrl = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(getEntityUrl())}`;
    window.open(mailtoUrl);
    handleClose();
  };

  const handleReferToUser = () => {
    setView('referral');
    setReferralMessage(`Hey! I wanted to share this awesome ${entityType} with you: ${entity?.name}. Check it out and let me know if you're interested!`);
  };

  const handleBackToOptions = () => {
    setView('options');
    setUserSearch('');
    setSelectedUsers([]);
    setReferralMessage('');
    setSearchResults([]);
    setHasSearched(false);
    setReferralStatus({ type: '', message: '' });
    setSendingReferrals(false);
  };

  const handleSelectUser = (user) => {
    const isSelected = selectedUsers.some(u => u.id === user.id);
    if (isSelected) {
      setSelectedUsers(selectedUsers.filter(u => u.id !== user.id));
    } else {
      setSelectedUsers([...selectedUsers, user]);
    }
  };

  const handleSendReferrals = async () => {
    if (selectedUsers.length === 0) {
      setReferralStatus({ type: 'error', message: `Please select at least one user to refer the ${entityType} to.` });
      return;
    }

    setSendingReferrals(true);
    setReferralStatus({ type: '', message: '' });

    try {
      const entityUrl = getEntityUrl();
      const userNames = selectedUsers.map(u => u.name).join(', ');

      const notifications = selectedUsers.map(selectedUser => {
        const message = referralMessage.trim()
          ? `${selectedUser?.name || 'User'}, you've been invited to join "${entity?.name}"! Message: ${referralMessage}`
          : `${selectedUser?.name || 'User'}, you've been invited to join "${entity?.name}"!`;

        return notificationAPI.create({
          message: message,
          userId: selectedUser.id,
          webLink: entityUrl,
          status: 'unread',
          priority: 'high',
          createdBy: currentUser?.id || 1
        });
      });

      await Promise.all(notifications);

      setReferralStatus({
        type: 'success',
        message: `Successfully sent ${entityType} referral to ${selectedUsers.length} user${selectedUsers.length > 1 ? 's' : ''}: ${userNames}`
      });

      setTimeout(() => {
        setSelectedUsers([]);
        setReferralMessage('');
        setReferralStatus({ type: '', message: '' });
        handleClose();
      }, 2000);

    } catch (error) {
      console.error('Failed to send referrals:', error);
      setReferralStatus({
        type: 'error',
        message: `Failed to send referrals. Please try again. Error: ${error.message || 'Unknown error'}`
      });
    } finally {
      setSendingReferrals(false);
    }
  };

  // Native apps use the OS share sheet (triggered in useEffect above)
  if (!isOpen || !entity || Capacitor.isNativePlatform()) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className={`${theme.card} rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto`}>
        {/* Share Options View */}
        {view === 'options' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div>
                <h3 className={`text-lg font-semibold ${theme.text}`}>Share {getEntityDisplayName()}</h3>
                <p className={`text-sm ${theme.textSecondary}`}>{entity?.name}</p>
              </div>
              <button
                onClick={handleClose}
                className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
              >
                <X className={`w-5 h-5 ${theme.textMuted}`} />
              </button>
            </div>

            <div className="space-y-3">
              <button
                onClick={handleCopyLink}
                className={`w-full flex items-center gap-x-3 p-4 ${theme.backgroundSecondary} rounded-lg ${theme.cardHover} transition-colors`}
              >
                <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                  <Link className="w-5 h-5 text-blue-600" />
                </div>
                <div className="text-left">
                  <div className={`font-medium ${theme.text}`}>Copy Link</div>
                  <div className={`text-sm ${theme.textSecondary}`}>Copy {entityType} link to clipboard</div>
                </div>
              </button>

              <button
                onClick={handleSendToPhone}
                className={`w-full flex items-center gap-x-3 p-4 ${theme.backgroundSecondary} rounded-lg ${theme.cardHover} transition-colors`}
              >
                <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                  <Smartphone className="w-5 h-5 text-green-600" />
                </div>
                <div className="text-left">
                  <div className={`font-medium ${theme.text}`}>Send to Phone</div>
                  <div className={`text-sm ${theme.textSecondary}`}>Share via SMS or messaging app</div>
                </div>
              </button>

              <button
                onClick={() => {
                  window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(getEntityUrl())}`, '_blank', 'noopener,width=600,height=400');
                }}
                className={`w-full flex items-center gap-x-3 p-4 ${theme.backgroundSecondary} rounded-lg ${theme.cardHover} transition-colors`}
              >
                <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">f</span>
                </div>
                <div className="text-left">
                  <div className={`font-medium ${theme.text}`}>Share to Facebook</div>
                  <div className={`text-sm ${theme.textSecondary}`}>Post this {entityType} on Facebook</div>
                </div>
              </button>

              <button
                onClick={handleSendToEmail}
                className={`w-full flex items-center gap-x-3 p-4 ${theme.backgroundSecondary} rounded-lg ${theme.cardHover} transition-colors`}
              >
                <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                  <Mail className="w-5 h-5 text-orange-600" />
                </div>
                <div className="text-left">
                  <div className={`font-medium ${theme.text}`}>Send to Email</div>
                  <div className={`text-sm ${theme.textSecondary}`}>Share via email invitation</div>
                </div>
              </button>

              <button
                onClick={handleReferToUser}
                className={`w-full flex items-center gap-x-3 p-4 ${theme.backgroundSecondary} rounded-lg ${theme.cardHover} transition-colors`}
              >
                <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                  <UserPlus className="w-5 h-5 text-purple-600" />
                </div>
                <div className="text-left">
                  <div className={`font-medium ${theme.text}`}>Refer to User</div>
                  <div className={`text-sm ${theme.textSecondary}`}>Find a user and refer this {entityType}</div>
                </div>
              </button>
            </div>
          </>
        )}

        {/* Send to Phone View */}
        {view === 'phone' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-x-3">
                <button
                  onClick={() => setView('options')}
                  className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
                >
                  <ArrowLeft className={`w-5 h-5 ${theme.textMuted}`} />
                </button>
                <div>
                  <h3 className={`text-lg font-semibold ${theme.text}`}>Send to Phone</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>{entity?.name}</p>
                </div>
              </div>
              <button onClick={handleClose} className={`p-2 ${theme.cardHover} rounded-full transition-colors`}>
                <X className={`w-5 h-5 ${theme.textMuted}`} />
              </button>
            </div>

            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                  Phone Number
                </label>
                <input
                  type="tel"
                  value={phoneNumber}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className={`w-full px-4 py-3 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent text-lg`}
                  onKeyDown={(e) => e.key === 'Enter' && handleSendSms()}
                  autoFocus
                />
                <p className={`mt-1 text-xs ${theme.textMuted}`}>
                  Opens your SMS app with the link pre-filled
                </p>
              </div>

              <div className="flex gap-x-3">
                <button
                  onClick={() => setView('options')}
                  className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
                >
                  Back
                </button>
                <button
                  onClick={handleSendSms}
                  disabled={!phoneNumber.trim()}
                  className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-x-2`}
                >
                  <Smartphone className="w-4 h-4" />
                  <span>Send SMS</span>
                </button>
              </div>
            </div>
          </>
        )}

        {/* User Referral View */}
        {view === 'referral' && (
          <>
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-x-3">
                <button
                  onClick={handleBackToOptions}
                  className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
                >
                  <ArrowLeft className={`w-5 h-5 ${theme.textMuted}`} />
                </button>
                <div>
                  <h3 className={`text-lg font-semibold ${theme.text}`}>Refer to Users</h3>
                  <p className={`text-sm ${theme.textSecondary}`}>{entity?.name}</p>
                </div>
              </div>
              <button
                onClick={handleClose}
                className={`p-2 ${theme.cardHover} rounded-full transition-colors`}
              >
                <X className={`w-5 h-5 ${theme.textMuted}`} />
              </button>
            </div>

            {/* User Search */}
            <div className="mb-4">
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search users by name, email, or location..."
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  className={`w-full pl-4 pr-12 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                />
                <button
                  onClick={searchUsers}
                  type="button"
                  className={`absolute right-3 top-1/2 transform -translate-y-1/2 p-1 ${theme.cardHover} rounded transition-colors hover:bg-blue-50`}
                  title="Search users"
                >
                  <Search className={`w-5 h-5 ${theme.textMuted} hover:text-blue-500 transition-colors`} />
                </button>
              </div>
            </div>

            {/* Selected Users */}
            {selectedUsers.length > 0 && (
              <div className="mb-4">
                <h4 className={`text-sm font-medium ${theme.text} mb-2`}>
                  Selected ({selectedUsers.length})
                </h4>
                <div className="flex flex-wrap gap-2">
                  {selectedUsers.map(user => (
                    <div
                      key={user.id}
                      className={`flex items-center gap-x-2 px-3 py-1 rounded-full text-sm border border-blue-500 text-blue-500 ${theme.backgroundSecondary}`}
                    >
                      <span>{user.avatar}</span>
                      <span className={theme.text}>{user.name}</span>
                      <button
                        onClick={() => handleSelectUser(user)}
                        className={`${theme.cardHover} rounded-full p-0.5 transition-colors`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* User List */}
            <div className="mb-4 max-h-60 overflow-y-auto">
              {!hasSearched ? (
                <div className="text-center py-8">
                  <p className={`text-sm ${theme.textSecondary}`}>
                    Enter a name, email, or location and click search to find users
                  </p>
                </div>
              ) : (
                <>
                  <h4 className={`text-sm font-medium ${theme.text} mb-2`}>
                    Search Results ({searchResults.length})
                  </h4>
                  {usersLoading ? (
                    <div className="text-center py-8">
                      <div className="animate-spin w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2"></div>
                      <div className={`text-sm ${theme.textSecondary}`}>Searching users...</div>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      {searchResults.map(user => {
                        const isSelected = selectedUsers.some(u => u.id === user.id);
                        return (
                          <button
                            key={user.id}
                            onClick={() => handleSelectUser(user)}
                            className={`w-full flex items-center gap-x-3 p-3 rounded-lg transition-colors border-2 ${
                              isSelected
                                ? `${theme.backgroundSecondary} border-blue-500`
                                : `${theme.backgroundSecondary} ${theme.cardHover} border-transparent`
                            }`}
                          >
                            <span className="text-xl">{user.avatar}</span>
                            <div className="text-left flex-1">
                              <div className={`font-medium ${theme.text}`}>{user.name}</div>
                              <div className={`text-sm ${theme.textSecondary}`}>
                                {user.email} • {user.location}
                              </div>
                            </div>
                            {isSelected && (
                              <div className="w-5 h-5 bg-blue-500 rounded-full flex items-center justify-center">
                                <div className="w-2 h-2 bg-white rounded-full"></div>
                              </div>
                            )}
                          </button>
                        );
                      })}
                      {searchResults.length === 0 && !usersLoading && (
                        <div className="text-center py-8">
                          <div className={`${theme.textMuted} mb-2`}>No users found</div>
                          <div className={`text-sm ${theme.textSecondary}`}>
                            No users match your search criteria or the server is unavailable. Try different terms or check back later.
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Referral Message */}
            <div className="mb-4">
              <label className={`block text-sm font-medium ${theme.text} mb-2`}>
                Personal Message ({referralMessage.length}/100)
              </label>
              <textarea
                value={referralMessage}
                onChange={(e) => {
                  if (e.target.value.length <= 100) {
                    setReferralMessage(e.target.value);
                  }
                }}
                placeholder="Add a personal message to your referral..."
                rows="3"
                maxLength="100"
                className={`w-full px-3 py-2 border rounded-lg ${theme.input} focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
              />
              {referralMessage.length >= 90 && (
                <div className={`text-xs mt-1 ${referralMessage.length === 100 ? 'text-red-500' : 'text-yellow-600'}`}>
                  {referralMessage.length === 100 ? 'Character limit reached' : `${100 - referralMessage.length} characters remaining`}
                </div>
              )}
            </div>

            {/* Status Messages */}
            {referralStatus.message && (
              <div className={`p-3 rounded-lg mb-4 ${
                referralStatus.type === 'success'
                  ? 'bg-green-100 text-green-800 border border-green-300'
                  : 'bg-red-100 text-red-800 border border-red-300'
              }`}>
                <p className="text-sm font-medium">{referralStatus.message}</p>
              </div>
            )}

            {/* Send Button */}
            <div className="flex gap-x-3">
              <button
                onClick={handleBackToOptions}
                className={`flex-1 ${btn.base} ${btn.md} ${theme.buttonGhost}`}
              >
                Back
              </button>
              <button
                onClick={handleSendReferrals}
                disabled={selectedUsers.length === 0 || sendingReferrals}
                className={`${btn.base} ${btn.md} ${theme.buttonPrimary} flex-1 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-x-2`}
              >
                {sendingReferrals ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Sending...</span>
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    <span>Send Referrals ({selectedUsers.length})</span>
                  </>
                )}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
